export interface ConfigFile {
  id: string;
  /** i18n key of the card title (translated at render time). */
  titleKey: string;
  filename: string;
  /** i18n key of the card description (translated at render time). */
  descriptionKey: string;
  format: string;
  accept: string;
  /** i18n keys of the spec bullet points (translated at render time). */
  specKeys: string[];
  content?: string;
  file?: File | null;
  uploaded: boolean;
  loading: boolean;
  error?: string | null;
}

export interface SectorTemplate {
  id: string;
  name: string;
  description: string;
  file_count: number;
}

export const CONFIG_GROUPS = {
  ontologie: ['ontology_owl', 'ontology_schema'],
  themes: ['themes', 'domain_entity_mapping'],
  thesaurus: ['thesaurus', 'entity_type_variations'],
  prompts: ['prompts', 'system_messages', 'rag_prompts', 'entity_extraction_prompts', 'script_prompts'],
  documents: ['source_types', 'reference_texts'],
} as const;

export type ConfigGroupKey = keyof typeof CONFIG_GROUPS;

export const STRUCTURED_EDITOR_IDS = [
  'ontology_schema',
  'thesaurus',
  'prompts',
  'themes',
  'domain_entity_mapping',
  'entity_type_variations',
  'source_types',
  'system_messages',
  'rag_prompts',
  'entity_extraction_prompts',
  'script_prompts',
  'ontology_owl',
  'reference_texts',
] as const;

/**
 * Config files the backend still stores, validates and templates, but never reads at runtime:
 * `load_organization_config()` parses them into the config dict and nothing consumes those keys.
 * Hidden from the UI so admins are not asked to maintain — or warned about — files that have no
 * effect. Only the display layer ignores them; upload routes and templates are untouched.
 */
export const UNUSED_FILE_IDS = ['ontology_schema', 'prompts', 'script_prompts', 'reference_texts'] as const;

/** Config file ids shown only when "Advanced" mode is enabled (simple mode hides these cards). */
export const ADVANCED_ONLY_FILE_IDS = [
  'ontology_schema',
  'domain_entity_mapping',
  'rag_prompts',
  'entity_extraction_prompts',
  'script_prompts',
] as const;

export type StructuredEditorId = (typeof STRUCTURED_EDITOR_IDS)[number];

export const isStructuredEditorId = (fileId: string): fileId is StructuredEditorId =>
  (STRUCTURED_EDITOR_IDS as readonly string[]).includes(fileId);
